import React from "react";
import {
  Shield,
  Search,
  Clock,
  TrendingUp,
  Smartphone,
  MessageSquare,
  Globe,
  MapPin,
  MousePointerClick,
} from "lucide-react";
import PageHero from "../components/PageHero";
import { Section, SectionHeading, Check, Eyebrow } from "../components/SectionPrimitives";
import CTABand from "../components/CTABand";

const included = [
  "Design profissional e exclusivo",
  "Site 100% responsivo (mobile / tablet / desktop)",
  "Integração direta com WhatsApp",
  "Formulário de contato inteligente",
  "SEO otimizado para Google",
  "Certificado SSL (cadeado de segurança)",
  "Hospedagem de alta performance",
  "Integração com Google Maps",
  "Botões de conversão em locais estratégicos",
];

const benefits = [
  { icon: Shield, title: "Mais Credibilidade", desc: "Fortaleça sua marca e transmita confiança a quem visita seu site.", color: "#4285F4" },
  { icon: Search, title: "Presença no Google", desc: "Apareça para pessoas que estão procurando seus serviços agora.", color: "#EA4335" },
  { icon: Clock, title: "Atendimento 24h", desc: "Receba contatos a qualquer momento, mesmo fora do horário comercial.", color: "#FBBC05" },
  { icon: TrendingUp, title: "Mais Vendas", desc: "Transforme visitantes em clientes com uma jornada otimizada para conversão.", color: "#34A853" },
];

const types = [
  { label: "Sites Institucionais", icon: Globe },
  { label: "Landing Pages", icon: MousePointerClick },
  { label: "Sites para Advogados", icon: Shield },
  { label: "Sites para Clínicas", icon: Smartphone },
  { label: "Sites para Imobiliárias", icon: MapPin },
  { label: "Sites para Prestadores de Serviços", icon: MessageSquare },
  { label: "Sites Corporativos", icon: TrendingUp },
];

export default function CriacaoSites() {
  return (
    <div data-testid="page-sites">
      <PageHero
        breadcrumb="Criação de Sites"
        eyebrow="Desenvolvimento Web"
        title="Sites profissionais que geram"
        highlight="clientes."
        subtitle="Seu site é o cartão de visitas digital da sua empresa. Desenvolvemos sites modernos, rápidos e otimizados para conversão e posicionamento no Google."
      />

      {/* O que está incluso */}
      <Section testId="sites-included">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow color="#34A853">O que está incluso</Eyebrow>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-[#000000] mt-4 leading-[1.05]">
              Tudo o que sua empresa precisa para começar a vender online.
            </h2>
            <p className="mt-6 text-[#000000]/70 leading-relaxed">
              Cada projeto inclui um pacote completo, sem custos escondidos —
              do design à hospedagem, da segurança ao SEO.
            </p>
          </div>
          <div className="lg:col-span-7">
            <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-5">
              {included.map((it, i) => (
                <Check key={it} color={["#4285F4", "#EA4335", "#FBBC05", "#34A853"][i % 4]}>
                  {it}
                </Check>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Benefícios */}
      <Section testId="sites-benefits" className="bg-[#F8F9FA] border-y border-gray-100">
        <SectionHeading
          eyebrow="Benefícios"
          title="O impacto de um site profissional."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {benefits.map((b) => {
            const Icon = b.icon;
            return (
              <div key={b.title} className="og-card bg-white" data-testid={`benefit-${b.title}`}>
                <Icon size={26} style={{ color: b.color }} />
                <h3 className="font-display text-lg font-bold text-[#000000] mt-5">
                  {b.title}
                </h3>
                <p className="mt-3 text-sm text-[#000000]/70 leading-relaxed">
                  {b.desc}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* Tipos de Sites */}
      <Section testId="sites-types">
        <SectionHeading
          eyebrow="Tipos de Sites"
          title="Para cada segmento, um site sob medida."
          subtitle="Trabalhamos com diferentes formatos, sempre com foco em performance e conversão."
        />
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-100 border border-gray-100">
          {types.map((t, i) => {
            const Icon = t.icon;
            const colors = ["#4285F4", "#EA4335", "#FBBC05", "#34A853"];
            return (
              <div
                key={t.label}
                data-testid={`site-type-${i}`}
                className="bg-white p-8 flex items-center gap-4 hover:bg-[#F8F9FA] transition"
              >
                <span
                  className="w-10 h-10 inline-flex items-center justify-center"
                  style={{ background: colors[i % 4] + "15" }}
                >
                  <Icon size={20} style={{ color: colors[i % 4] }} />
                </span>
                <p className="font-display font-semibold text-[#000000]">
                  {t.label}
                </p>
              </div>
            );
          })}
        </div>
      </Section>

      <CTABand
        title="Solicite um orçamento e tenha um site profissional."
        subtitle="Envie seu projeto. Nosso time entrará em contato em até 1 dia útil com uma proposta personalizada."
        whatsappMsg="Olá! Tenho interesse em um orçamento para criação de site."
      />
    </div>
  );
}
